import type { HTMLAttributes } from 'react';
import type { Suit } from '@cribbage/engine';
import { cx } from './cx.js';

/**
 * The four suits in a quiet colored row: the Arcade's brand motif.
 *
 * It is worn above the wordmark and used as a divider, which is the whole of
 * its job — it says "cribbage" without a word and without a logo. Spade and
 * club are ink, heart is tangerine and diamond is teal, so the two team hues
 * appear at their smallest legitimate size. That is the same rule the rest of
 * the brand follows: teal and tangerine are ticks, washes and glyphs, never a
 * solid field.
 *
 * It is a component rather than four inline `<svg>`s for the reason rung 3
 * asks about: it supplies something a caller would forget. A decorative
 * ornament with no `aria-hidden` is four unlabelled graphics a screen reader
 * walks through on the way to the title, and the sketch it came from wrote
 * that attribute by hand on every copy. Here it is the default, and a caller
 * who genuinely wants it named passes `aria-hidden={undefined}` with a role.
 *
 * The paths are the sketch's, unchanged. Ornaments are drawn inline; this
 * client has no icon library and is not getting one. `SuitGlyph` below is the
 * single-suit export, so the one other place the brand draws a suit — the
 * face of a `MenuTile` door — wears these drawings rather than the font's
 * characters.
 */

export type SuitLineSize = 'xs' | 'sm' | 'md' | 'lg';

export type SuitLineTone =
  /** The motif as the brand draws it: heart tangerine, diamond teal. */
  | 'color'
  /** All four in ink, for a place the accents would shout. */
  | 'ink';

/*
 * The passthrough is the aria opt-out and nothing else. The header above
 * promises exactly one escape — a caller who genuinely wants the line named
 * passes `aria-hidden={undefined}` with a role — and four attributes are all
 * that promise needs. A whole `HTMLAttributes` spread was four attributes and
 * every other one nobody asked for, on a component whose point is that the
 * defaults are not the caller's to forget.
 */
export interface SuitLineProps extends Pick<
  HTMLAttributes<HTMLSpanElement>,
  'role' | 'aria-hidden' | 'aria-label' | 'id'
> {
  readonly size?: SuitLineSize;
  readonly tone?: SuitLineTone;
  /** Placement and spacing. The glyphs and their colors are not yours. */
  readonly className?: string | undefined;
}

/*
 * Glyph and gap together, because they are one decision. A gap that stays put
 * while the glyph grows stops reading as a line and starts reading as four
 * marks, and the sizes are far enough apart for that to happen inside one
 * screen.
 *
 * The ladder gained a fourth step on 2026-09-01, when the Arcade sketches were
 * moved off their own copy of these drawings. All three of them had been
 * hand-tuned to about a 0.75 gap-to-glyph ratio — 14/18, 11/15, 12/16 — and no
 * step here carried it: `xs` sat at 0.33 and `md` at 0.50, so the airiest
 * line the sketches drew had nowhere to land. `sm` is that ratio. The old `sm`
 * kept its two values and became `xs`, so the ladder reads in order rather
 * than hiding a new step in the middle of the old names.
 *
 * All sizes now use standard Tailwind spacing. The gap-to-glyph ratios are
 * 0.25 at `xs`, 0.75 at `sm`, 0.40 at `md`, and one third at `lg`.
 */
const SIZE = {
  xs: { row: 'gap-1', glyph: 'size-4' },
  sm: { row: 'gap-3', glyph: 'size-4' },
  md: { row: 'gap-2', glyph: 'size-5' },
  lg: { row: 'gap-2', glyph: 'size-6' },
} satisfies Record<SuitLineSize, { readonly row: string; readonly glyph: string }>;

/** A suit, by name. The union every suit-wearing prop shares. */
export type SuitName = 'spade' | 'heart' | 'diamond' | 'club';

const SPADE =
  'M12 2C16.6 6.3 20 9.4 20 13.1 20 15.7 18.1 17.6 15.8 17.6 14.7 17.6 13.7 17.2 12.9 16.5 13.2 18.2 13.9 19.7 15 21H9C10.1 19.7 10.8 18.2 11.1 16.5 10.3 17.2 9.3 17.6 8.2 17.6 5.9 17.6 4 15.7 4 13.1 4 9.4 7.4 6.3 12 2Z';
const HEART =
  'M12 21C6.9 16.6 3 13.4 3 9.1 3 6.1 5.2 4 7.7 4 9.4 4 10.9 4.9 12 6.4 13.1 4.9 14.6 4 16.3 4 18.8 4 21 6.1 21 9.1 21 13.4 17.1 16.6 12 21Z';
const DIAMOND = 'M12 2 20 12 12 22 4 12Z';
const CLUB_STEM = 'M12 12.5C12.2 15.7 13 18.3 14.8 21H9.2C11 18.3 11.8 15.7 12 12.5Z';

/** The sketch's drawings, one per suit, on a 24-unit box. */
const SHAPES = {
  spade: <path d={SPADE} />,
  heart: <path d={HEART} />,
  diamond: <path d={DIAMOND} />,
  club: (
    <g>
      <circle cx="12" cy="7.4" r="4.5" />
      <circle cx="6.6" cy="14" r="4.5" />
      <circle cx="17.4" cy="14" r="4.5" />
      <path d={CLUB_STEM} />
    </g>
  ),
} satisfies Record<SuitName, React.JSX.Element>;

export interface SuitGlyphProps {
  readonly suit: SuitName;
  /** Size and color, via `size-*` and `text-*`. The drawing is not yours. */
  readonly className?: string | undefined;
}

/**
 * One suit from the motif, filled with `currentColor`.
 *
 * Exported for the places that wear a single suit rather than the line — the
 * `MenuTile` door is the wearer — so the brand's suits are drawn once.
 * Decorative and unlabelled like the line; a parent that needs it named wraps
 * it.
 */
export function SuitGlyph({ suit, className }: SuitGlyphProps): React.JSX.Element {
  return (
    <svg
      className={cx('block shrink-0', className)}
      viewBox="0 0 24 24"
      fill="currentColor"
      focusable="false"
      aria-hidden="true"
    >
      {SHAPES[suit]}
    </svg>
  );
}

/**
 * Each suit's hue in the `color` tone. The `ink` tone overrides all four.
 *
 * Exported because `/dev/lookbook` names these colors beside the specimen, and
 * a workshop that hand-copies the classes is the design system describing
 * itself from memory. One table, read by the line and by the page about it.
 */
export const SUIT_TINTS = {
  spade: 'text-ink',
  heart: 'text-tangerine',
  diamond: 'text-teal',
  club: 'text-ink',
} satisfies Record<SuitName, string>;

/* The engine's suit numbers, in `SUIT_LETTERS` order: club, diamond, heart,
   spade. Not the motif's order, which is `SUIT_LINE` below. */
const ENGINE_SUITS: readonly SuitName[] = ['club', 'diamond', 'heart', 'spade'];

/**
 * The motif's name for an engine suit number, for a card surface drawing its
 * suit with `SuitGlyph`. One table, so no surface restates the engine's order.
 */
export function suitName(suit: number): SuitName {
  return ENGINE_SUITS[suit] ?? 'spade';
}

/** The engine's suit number for a motif suit: `suitName` read backwards. */
export function engineSuit(suit: SuitName): Suit {
  return ENGINE_SUITS.indexOf(suit) as Suit;
}

/**
 * The motif's own order, which the `MenuTile` doors and the Scoring tray deal
 * in too. Written out rather than read off `SUIT_TINTS`'s keys: the order is a
 * brand decision, and it should not rest on how an object literal happens to
 * enumerate.
 */
export const SUIT_LINE: readonly SuitName[] = ['spade', 'heart', 'diamond', 'club'];

export function SuitLine({
  size = 'md',
  tone = 'color',
  className,
  ...rest
}: SuitLineProps): React.JSX.Element {
  const step = SIZE[size];

  return (
    <span
      className={cx('inline-flex items-center', step.row, className)}
      aria-hidden="true"
      {...rest}
    >
      {SUIT_LINE.map((suit) => (
        <SuitGlyph
          key={suit}
          suit={suit}
          className={cx(step.glyph, tone === 'ink' ? 'text-ink' : SUIT_TINTS[suit])}
        />
      ))}
    </span>
  );
}
